/**
 * Shopify line-item properties from the MGP cart object.
 * The same object is the cart properties and the product body HTML.
 * Keys starting with "_" stay on the order and are hidden by the theme.
 * custom_image is the product image, not a property. data: URLs are omitted.
 * customURL is stored as hidden _customURL. An https link is kept.
 */

const PROPERTY_LABELS = [
  ["year", "Year"],
  ["make", "Make"],
  ["model", "Model"],
  ["submodel", "Submodel"],
  ["part_number", "Part Number"],
  ["plate_code_front", "Front Plate"],
  ["plate_code_rear", "Rear Plate"],
  ["plate_number_front", "Front Plate Number"],
  ["plate_number_rear", "Rear Plate Number"],
  ["options[color]", "Color"],
  ["options[paint_name]", "Paint Name"],
  ["options[paint_code]", "Paint Code"],
  ["options[design-front-type]", "Front Design"],
  ["options[design-rear-type]", "Rear Design"],
  ["options[design-front-image]", "Front Engraving"],
  ["options[design-rear-image]", "Rear Engraving"],
  ["options[design-front-fill]", "Front Fill"],
  ["options[design-rear-fill]", "Rear Fill"],
  ["options[design-front-text]", "Front Text"],
  ["options[design-rear-text]", "Rear Text"],
  ["options[design-front-font]", "Front Font"],
  ["options[design-rear-font]", "Rear Font"],
  ["options[design-choice]", "Design Choice"],
  ["options[bolts-bolts-choice]", "Bolts"],
  ["options[bolts-bolts-color]", "Bolt Color"],
  ["options[design-front-image-custom]", "Front Custom Image"],
  ["options[design-rear-image-custom]", "Rear Custom Image"],
  ["options[lit-texture-src]", "Pattern Image"],
];

const HIDDEN_LABELS = new Set([
  "Part Number",
  "Front Plate",
  "Rear Plate",
  "Front Plate Number",
  "Rear Plate Number",
  "Design Choice",
  "Finish",
  "Front Custom Image",
  "Rear Custom Image",
  "Pattern Image",
]);

function labelFor(label) {
  return HIDDEN_LABELS.has(label) ? `_${label}` : label;
}

const SIDE_LABELS = new Set(["Front Design", "Rear Design", "Front Fill", "Rear Fill"]);
const ALL_CAPS_MAKES = new Set(["bmw", "gmc"]);
const ALL_CAPS_WORDS = new Set(["mgp", "lit"]);

function engravingDisplayName(value) {
  const marker = "---";
  const index = value.lastIndexOf(marker);
  const name = index === -1 ? value : value.slice(index + marker.length);
  return name.replace(/\s+\((?:Mgp approved|Licensed)\)\s*$/, "").replace(/_/g, " ").trim();
}

function titleCaseWords(value) {
  return value
    .trim()
    .split(/(\s+|-)/)
    .map((part) => {
      if (!part || part === "-" || /^\s+$/.test(part)) return part;
      if (ALL_CAPS_WORDS.has(part.toLowerCase())) return part.toUpperCase();
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join("");
}

function titleCaseMake(value) {
  const trimmed = value.trim();
  if (ALL_CAPS_MAKES.has(trimmed.toLowerCase())) return trimmed.toUpperCase();
  return titleCaseWords(trimmed);
}

function stripSideSuffix(value) {
  return value.replace(/\s+\((?:Front|Rear)\)\s*$/, "");
}

function displayValue(label, value) {
  if (value == null) return "";
  let text = typeof value === "string" ? value : String(value);
  if (label === "Front Engraving" || label === "Rear Engraving") text = engravingDisplayName(text);
  if (label === "Make") text = titleCaseMake(text);
  if (label === "Model" || label === "Submodel") text = titleCaseWords(text);
  if (label === "Color") text = text.replace(/\(Lit\)$/i, "(LIT)");
  if (SIDE_LABELS.has(label)) text = stripSideSuffix(text);
  return text.trim();
}

function isOmitted(label, value) {
  if (value === "" || value.indexOf("data:") === 0 || value === "None") return true;
  if (label === "Front Design" && value === "None (Front)") return true;
  if (label === "Rear Design" && value === "None (Rear)") return true;
  return false;
}

export function mgpCartToLineItemProperties(cartObj, { licensed, customURL } = {}) {
  const properties = {
    [labelFor("Finish")]: licensed ? "Engraved Licensed" : "Plain",
  };

  PROPERTY_LABELS.forEach(([key, label]) => {
    const value = displayValue(label, cartObj?.[key]);
    if (isOmitted(label, value)) return;
    properties[labelFor(label)] = value;
  });

  const configurationUrl = displayValue("_customURL", customURL);
  if (!isOmitted("_customURL", configurationUrl)) properties._customURL = configurationUrl;

  if (cartObj?._msrp != null && cartObj._msrp !== "") properties._msrp = String(cartObj._msrp);

  return properties;
}
